import dayjs from "dayjs";

import type { Flight, FlightLeg } from "../slices/reservationSlice";

export type FlightPair = { outbound: Flight; inbound?: Flight };

const bySequenceThenDate = (a: FlightLeg, b: FlightLeg) => {
  if (a.sequence !== b.sequence) {
    return a.sequence - b.sequence;
  }

  const ad = a.departure_datetime
    ? dayjs(a.departure_datetime).valueOf()
    : Infinity;

  const bd = b.departure_datetime
    ? dayjs(b.departure_datetime).valueOf()
    : Infinity;

  return ad - bd;
};

const firstLeg = (f: Flight): FlightLeg | undefined => {
  const legs = Array.isArray(f.legs) ? [...f.legs] : [];

  if (!legs.length) {
    return undefined;
  }

  legs.sort(bySequenceThenDate);

  return legs[0];
};

const lastLeg = (f: Flight): FlightLeg | undefined => {
  const legs = Array.isArray(f.legs) ? [...f.legs] : [];

  if (!legs.length) {
    return undefined;
  }

  legs.sort(bySequenceThenDate);

  return legs[legs.length - 1];
};

const outKey = (f: Flight) => {
  const fl = firstLeg(f);
  const ll = lastLeg(f);

  if (!fl || !ll || !fl.departure_airport || !ll.arrival_airport) {
    return "";
  }

  return `${fl.departure_airport}→${ll.arrival_airport}`;
};

const inKey = (f: Flight) => {
  const fl = firstLeg(f);
  const ll = lastLeg(f);

  if (!fl || !ll || !ll.arrival_airport || !fl.departure_airport) {
    return "";
  }

  return `${ll.arrival_airport}→${fl.departure_airport}`;
};

export function pairFlights(flights: Flight[] = []): FlightPair[] {
  const list = [...flights].sort((a, b) => {
    const aFirst = firstLeg(a);
    const bFirst = firstLeg(b);

    const ad = aFirst?.departure_datetime
      ? dayjs(aFirst.departure_datetime).valueOf()
      : Infinity;

    const bd = bFirst?.departure_datetime
      ? dayjs(bFirst.departure_datetime).valueOf()
      : Infinity;

    if (ad !== bd) {
      return ad - bd;
    }

    return (a.id ?? 0) - (b.id ?? 0);
  });

  const used = new Set<number>();
  const pairs: FlightPair[] = [];

  for (let i = 0; i < list.length; i++) {
    if (used.has(i)) {
      continue;
    }

    const f = list[i];
    const ik = inKey(f);

    let inboundIndex: number | undefined;

    if (ik) {
      for (let j = i + 1; j < list.length; j++) {
        if (used.has(j)) {
          continue;
        }

        if (outKey(list[j]) === ik) {
          inboundIndex = j;
          break;
        }
      }
    }

    pairs.push({
      outbound: f,
      inbound: inboundIndex != null ? list[inboundIndex] : undefined,
    });

    used.add(i);

    if (inboundIndex != null) {
      used.add(inboundIndex);
    }
  }

  return pairs;
}
